import React, { useState, useEffect, useCallback } from 'react';
import {
  IconDatabaseExport, IconPlus, IconDownload, IconTrash, IconRotateClockwise,
  IconDatabase, IconFolder, IconStack2, IconCheck, IconX, IconLoader, IconAlertTriangle
} from '@tabler/icons-react';
import api from '../api/api';

const SCOPE_OPTIONS = [
  { value: 'FULL', label: '전체 백업', desc: 'DB + 업로드 파일', icon: IconStack2, color: 'primary' },
  { value: 'DB_ONLY', label: 'DB만', desc: '데이터베이스만 백업', icon: IconDatabase, color: 'blue' },
  { value: 'FILES_ONLY', label: '파일만', desc: '업로드 파일만 백업', icon: IconFolder, color: 'cyan' },
];

const STATUS_MAP = {
  IN_PROGRESS: { label: '진행중', color: 'warning', icon: IconLoader },
  COMPLETED:   { label: '완료', color: 'success', icon: IconCheck },
  FAILED:      { label: '실패', color: 'danger', icon: IconX },
  RESTORED:    { label: '복원됨', color: 'info', icon: IconRotateClockwise },
};

export default function BackupPage({ me, showToast }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // 생성 모달
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', backupScope: 'FULL' });
  const [creating, setCreating] = useState(false);

  // 삭제 / 복원 확인
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backups', { params: { page, size: 20, sort: 'createdAt,desc' } });
      setBackups(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
    } catch (e) {
      showToast?.('백업 목록 조회 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, showToast]);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  // ── 백업 생성 ──
  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.post('/backups', createForm);
      if (res.data.status === 'COMPLETED') {
        showToast?.(`백업 완료: ${res.data.fileSizeFormatted}`, 'success');
      } else if (res.data.status === 'FAILED') {
        showToast?.(`백업 실패: ${res.data.errorMessage || '알 수 없는 오류'}`, 'error');
      }
      setCreateOpen(false);
      setCreateForm({ name: '', description: '', backupScope: 'FULL' });
      fetchBackups();
    } catch (e) {
      showToast?.(e.response?.data?.message || '백업 생성 실패', 'error');
    } finally {
      setCreating(false);
    }
  };

  // ── 다운로드 ──
  const handleDownload = async (backup) => {
    try {
      const res = await api.get(`/backups/${backup.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;

      // Content-Disposition에서 파일명 추출
      const disposition = res.headers['content-disposition'];
      let fileName = `backup_${backup.id}.zip`;
      if (disposition) {
        const match = disposition.match(/filename\*?=(?:UTF-8'')?(.+)/i);
        if (match) fileName = decodeURIComponent(match[1]);
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast?.('다운로드 실패', 'error');
    }
  };

  // ── 복원 ──
  const handleRestoreConfirm = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      await api.post(`/backups/${restoreTarget.id}/restore`);
      showToast?.('복원이 완료되었습니다. 페이지를 새로고침하세요.', 'success');
      setRestoreTarget(null);
      fetchBackups();
    } catch (e) {
      showToast?.(e.response?.data?.message || '복원 실패', 'error');
    } finally {
      setRestoring(false);
    }
  };

  // ── 삭제 ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/backups/${deleteTarget.id}`);
      showToast?.('백업이 삭제되었습니다.', 'success');
      setDeleteTarget(null);
      fetchBackups();
    } catch (e) {
      showToast?.('삭제 실패', 'error');
    }
  };

  const formatDate = (dt) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getScopeLabel = (scope) => {
    const opt = SCOPE_OPTIONS.find(s => s.value === scope);
    return opt ? opt.label : scope;
  };

  const getStatus = (status) => STATUS_MAP[status] || STATUS_MAP.FAILED;

  return (
    <div className="container-xl mt-4">
      <div className="page-wrapper">
        <div className="container-xl">
          {/* 헤더 */}
          <div className="page-header d-print-none">
            <div className="row align-items-center">
              <div className="col">
                <h2 className="page-title">백업 / 복원</h2>
                <div className="text-muted mt-1">데이터베이스 및 파일의 백업을 생성하고 복원합니다.</div>
              </div>
              <div className="col-auto">
                <button className="btn btn-sm btn-primary d-none d-sm-inline-block" onClick={() => setCreateOpen(true)}>
                  <IconPlus size={18} className="me-2" />
                  백업 생성
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            <div className="row row-deck row-cards">
              <div className="col-12">
                <div className="card">
                  <div className="table-responsive">
                    {loading ? (
                      <div className="p-4 text-center">로드 중...</div>
                    ) : backups.length === 0 ? (
                      <div className="p-4 text-center text-muted">
                        <IconDatabaseExport size={48} className="mb-3 opacity-50" />
                        <p>생성된 백업이 없습니다.</p>
                        <button className="btn btn-sm btn-primary" onClick={() => setCreateOpen(true)}>
                          첫 번째 백업 만들기
                        </button>
                      </div>
                    ) : (
                      <table className="table card-table table-vcenter text-nowrap">
                        <thead>
                          <tr>
                            <th>백업명</th>
                            <th>유형</th>
                            <th>범위</th>
                            <th>크기</th>
                            <th>상태</th>
                            <th>생성자</th>
                            <th>생성일시</th>
                            <th>복원일시</th>
                            <th style={{ width: '140px' }}>작업</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backups.map((backup) => {
                            const st = getStatus(backup.status);
                            return (
                              <tr key={backup.id}>
                                <td>
                                  <div className="fw-bold">{backup.name}</div>
                                  {backup.description && <div className="text-muted small">{backup.description}</div>}
                                </td>
                                <td>
                                  <span className={`badge bg-${backup.backupType === 'SCHEDULED' ? 'azure' : 'purple'}-lt`}>
                                    {backup.backupType === 'SCHEDULED' ? '정기' : '수동'}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-secondary-lt">{getScopeLabel(backup.backupScope)}</span>
                                </td>
                                <td>{backup.fileSizeFormatted || '-'}</td>
                                <td>
                                  <span className={`badge bg-${st.color}-lt d-inline-flex align-items-center gap-1`}>
                                    <st.icon size={12} /> {st.label}
                                  </span>
                                  {backup.status === 'FAILED' && backup.errorMessage && (
                                    <div className="text-danger small" title={backup.errorMessage}>
                                      {backup.errorMessage.substring(0, 30)}...
                                    </div>
                                  )}
                                </td>
                                <td>{backup.createdBy || '-'}</td>
                                <td>{formatDate(backup.createdAt)}</td>
                                <td>{formatDate(backup.restoredAt)}</td>
                                <td>
                                  <div className="d-flex align-items-center gap-1 flex-nowrap">
                                    {backup.status === 'COMPLETED' && (
                                      <>
                                        <button
                                          className="btn btn-icon btn-sm btn-ghost-primary"
                                          onClick={() => handleDownload(backup)}
                                          title="다운로드">
                                          <IconDownload size={16} />
                                        </button>
                                        <button
                                          className="btn btn-icon btn-sm btn-ghost-warning"
                                          onClick={() => setRestoreTarget(backup)}
                                          title="복원">
                                          <IconRotateClockwise size={16} />
                                        </button>
                                      </>
                                    )}
                                    <button
                                      className="btn btn-icon btn-sm btn-ghost-danger"
                                      onClick={() => setDeleteTarget(backup)}
                                      title="삭제">
                                      <IconTrash size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* 페이지네이션 */}
                  {totalPages > 1 && (
                    <div className="card-footer d-flex align-items-center justify-content-center">
                      <ul className="pagination m-0">
                        <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p => Math.max(0, p - 1))}>이전</button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => (
                          <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setPage(i)}>{i + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>다음</button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 백업 생성 모달 ── */}
      {createOpen && (
        <div className="modal modal-blur d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">백업 생성</h5>
                <button type="button" className="btn-close" onClick={() => setCreateOpen(false)} disabled={creating} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">백업 이름</label>
                    <input type="text" className="form-control form-control-sm"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder="비워두면 자동 생성 (예: 수동 백업 20260327_120000)" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">설명</label>
                    <textarea className="form-control form-control-sm" rows={2}
                      value={createForm.description}
                      onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                      placeholder="백업 메모 (선택)" />
                  </div>
                  <div className="col-12">
                    <label className="form-label">백업 범위</label>
                    <div className="row g-2">
                      {SCOPE_OPTIONS.map((opt) => (
                        <div key={opt.value} className="col-4">
                          <label className={`card card-sm cursor-pointer ${createForm.backupScope === opt.value ? 'border-primary' : ''}`}
                            style={{ cursor: 'pointer' }}>
                            <div className="card-body text-center p-3">
                              <input type="radio" name="backupScope" className="d-none"
                                checked={createForm.backupScope === opt.value}
                                onChange={() => setCreateForm({ ...createForm, backupScope: opt.value })} />
                              <opt.icon size={28} className={`mb-2 text-${opt.color}`} />
                              <div className="fw-bold small">{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{opt.desc}</div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setCreateOpen(false)} disabled={creating}>취소</button>
                <button className="btn btn-sm btn-primary" onClick={handleCreate} disabled={creating}>
                  {creating ? (
                    <><IconLoader size={16} className="me-2 spinner-border spinner-border-sm" /> 백업 중...</>
                  ) : (
                    <><IconDatabaseExport size={16} className="me-2" /> 백업 시작</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 복원 확인 모달 ── */}
      {restoreTarget && (
        <div className="modal modal-blur d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-warning">
                  <IconAlertTriangle size={20} className="me-2" />
                  백업 복원
                </h5>
                <button type="button" className="btn-close" onClick={() => setRestoreTarget(null)} disabled={restoring} />
              </div>
              <div className="modal-body">
                <p><strong>"{restoreTarget.name}"</strong> 백업으로 복원하시겠습니까?</p>
                <div className="alert alert-warning small">
                  <strong>⚠️ 주의:</strong> 현재 데이터가 백업 시점의 데이터로 덮어씌워집니다. 복원 전 현재 상태를 백업하는 것을 권장합니다.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setRestoreTarget(null)} disabled={restoring}>취소</button>
                <button className="btn btn-sm btn-warning" onClick={handleRestoreConfirm} disabled={restoring}>
                  {restoring ? '복원 중...' : '복원 실행'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 삭제 확인 모달 ── */}
      {deleteTarget && (
        <div className="modal modal-blur d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">백업 삭제</h5>
                <button type="button" className="btn-close" onClick={() => setDeleteTarget(null)} />
              </div>
              <div className="modal-body">
                <p><strong>"{deleteTarget.name}"</strong> 백업을 삭제하시겠습니까?</p>
                <p className="text-muted small">백업 파일도 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-ghost-secondary" onClick={() => setDeleteTarget(null)}>취소</button>
                <button className="btn btn-sm btn-danger" onClick={handleDeleteConfirm}>삭제</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
